import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannelMulti } from "@/lib/realtime-channel";
import { useWorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";
import { resolveFunnelDisplayName } from "@/lib/funnel-display-label";
import {
  fetchLeadFunnelPlacement,
  persistLeadFunnelPlacement,
  type LeadFunnelPlacement,
} from "@/lib/lead-funnel-placement";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";

export type { LeadFunnelPlacement };

type StageRow = {
  funnel_id: string | null;
  slug: string;
  name: string;
  color: string;
  order_index: number;
};

type FunnelRow = {
  id: string;
  name: string;
  color: string;
  integration_id: string | null;
};

interface ConversationLeadFunnelControlsProps {
  leadId: string;
  workspaceId: string | null;
  integrationId?: string | null;
  className?: string;
}

export function ConversationLeadFunnelControls({
  leadId,
  workspaceId,
  integrationId,
  className,
}: ConversationLeadFunnelControlsProps) {
  const { toast } = useToast();
  const integrationById = useWorkspaceIntegrationsMap(workspaceId);
  const [placement, setPlacement] = useState<LeadFunnelPlacement | null>(null);
  const [funnels, setFunnels] = useState<FunnelRow[]>([]);
  const [stages, setStages] = useState<StageRow[]>([]);
  const [funnelOpen, setFunnelOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const reloadPlacement = useCallback(async () => {
    if (!leadId) return;
    const next = await fetchLeadFunnelPlacement(supabase, leadId, integrationById, {
      workspaceId,
      preferIntegrationId: integrationId,
    });
    setPlacement(next);
  }, [leadId, integrationById, workspaceId, integrationId]);

  useEffect(() => {
    if (!leadId || !workspaceId) {
      setPlacement(null);
      return;
    }

    void reloadPlacement();

    const channelName = `conv-lead-funnel-ctrl-${leadId}`;
    const channel = subscribePostgresChannelMulti(channelName, [
      {
        binding: { event: "*", schema: "public", table: "lead_funnel_memberships", filter: `lead_id=eq.${leadId}` },
        callback: () => void reloadPlacement(),
      },
      {
        binding: { event: "UPDATE", schema: "public", table: "leads", filter: `id=eq.${leadId}` },
        callback: () => void reloadPlacement(),
      },
    ]);

    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [leadId, workspaceId, reloadPlacement]);

  useEffect(() => {
    if (!workspaceId) return;
    void (async () => {
      const [{ data: funnelRows }, { data: stageRows }] = await Promise.all([
        supabase
          .from("sales_funnels")
          .select("id, name, integration_id, color")
          .eq("workspace_id", workspaceId)
          .order("order_index", { ascending: true }),
        supabase
          .from("lead_stages")
          .select("funnel_id, slug, name, color, order_index")
          .eq("workspace_id", workspaceId)
          .order("order_index", { ascending: true }),
      ]);
      setFunnels(funnelRows || []);
      setStages(stageRows || []);
    })();
  }, [workspaceId]);

  const stagesForFunnel = placement
    ? stages.filter((s) => s.funnel_id === placement.funnelId)
    : [];

  const handleFunnelSelect = async (funnelId: string) => {
    if (placement?.funnelId === funnelId) {
      setFunnelOpen(false);
      return;
    }

    setSaving(true);
    try {
      const targetStages = stages.filter((s) => s.funnel_id === funnelId);
      const nextStage =
        (placement &&
          targetStages.find((s) => s.slug === placement.stageSlug)?.slug) ||
        targetStages[0]?.slug ||
        "novo";

      await persistLeadFunnelPlacement(supabase, leadId, funnelId, nextStage);
      await reloadPlacement();
      setFunnelOpen(false);
      toast({ title: "Funil atualizado" });
    } catch (error: unknown) {
      toast({
        title: "Erro ao mover funil",
        description: error instanceof Error ? error.message : "Tente novamente",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStageSelect = async (stageSlug: string) => {
    if (!placement || stageSlug === placement.stageSlug) {
      setStageOpen(false);
      return;
    }

    setSaving(true);
    try {
      await persistLeadFunnelPlacement(supabase, leadId, placement.funnelId, stageSlug);
      await reloadPlacement();
      setStageOpen(false);
      toast({ title: "Etapa atualizada" });
    } catch (error: unknown) {
      toast({
        title: "Erro ao mover etapa",
        description: error instanceof Error ? error.message : "Tente novamente",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const indicatorBtnClass =
    "h-7 min-w-0 shrink gap-1 border-border/60 px-1.5 text-[10px] font-normal shadow-none sm:h-8 sm:gap-1.5 sm:px-2 sm:text-xs max-w-[min(5.5rem,20vw)] min-[380px]:max-w-[min(7rem,24vw)] sm:max-w-[8rem] md:max-w-[10rem]";

  const currentStageColor =
    stagesForFunnel.find((s) => s.slug === placement?.stageSlug)?.color ||
    placement?.stageColor ||
    "bg-gray-500";

  const funnelLabel = placement?.funnelName || "Funil";
  const stageLabel = placement?.stageName || "Etapa";

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn("flex min-w-0 items-center gap-0.5 sm:gap-1", className)}>
      <Popover open={funnelOpen} onOpenChange={setFunnelOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                type="button"
                disabled={saving || funnels.length === 0}
                aria-label={`Funil: ${funnelLabel}`}
                className={indicatorBtnClass}
              >
                <span
                  className={cn(
                    "h-2 w-2 shrink-0 rounded-full",
                    funnels.find((f) => f.id === placement?.funnelId)?.color || placement?.funnelColor || "bg-blue-500",
                  )}
                />
                <span className="hidden min-[380px]:inline truncate">{funnelLabel}</span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="min-[380px]:hidden">
            {funnelLabel}
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="w-64 p-2" align="end" sideOffset={8}>
          <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Mover para funil</p>
          <div className="max-h-56 space-y-0.5 overflow-y-auto">
            {funnels.map((funnel) => {
              const label = resolveFunnelDisplayName(funnel, integrationById);
              const active = placement?.funnelId === funnel.id;
              return (
                <button
                  key={funnel.id}
                  type="button"
                  disabled={saving}
                  onClick={() => void handleFunnelSelect(funnel.id)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-muted",
                    active && "bg-primary/10 text-primary font-medium",
                  )}
                >
                  <span className={cn("h-2 w-2 shrink-0 rounded-full", funnel.color || "bg-blue-500")} />
                  {label}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      <Popover open={stageOpen} onOpenChange={setStageOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                type="button"
                disabled={saving || !placement || stagesForFunnel.length === 0}
                aria-label={`Etapa: ${stageLabel}`}
                className={indicatorBtnClass}
              >
                <span className={cn("h-2 w-2 shrink-0 rounded-full", currentStageColor)} />
                <span className="hidden min-[380px]:inline truncate">{stageLabel}</span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="min-[380px]:hidden">
            {stageLabel}
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="w-56 p-2" align="end" sideOffset={8}>
          <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Mover para coluna</p>
          <div className="max-h-56 space-y-0.5 overflow-y-auto">
            {stagesForFunnel.map((stage) => {
              const active = placement?.stageSlug === stage.slug;
              return (
                <button
                  key={stage.slug}
                  type="button"
                  disabled={saving}
                  onClick={() => void handleStageSelect(stage.slug)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-muted",
                    active && "bg-primary/10 text-primary font-medium",
                  )}
                >
                  <span className={cn("h-2 w-2 shrink-0 rounded-full", stage.color || "bg-gray-500")} />
                  {stage.name}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      </div>
    </TooltipProvider>
  );
}
